## CODE OF CONDUCT

Our goal with Toronto JS is to bring the Toronto programming community together
for events, meetups, and conferences about the excitement, joy, and surprise of
programming, especially Javascript!

We value the participation of each member of the community and want
everyone to have an enjoyable and fulfilling experience.
All participants of our online and in-person gatherings are expected to show respect
and courtesy to other attendees during any Toronto JS event, online discussion, meetup,
or conference, whether officially sponsored by Toronto JS or not.

All attendees, speakers, exhibitors, organizers and volunteers at any
Toronto JS event are expected to observe the following Code of
Conduct. Organizers will enforce this code through the community and throughout events.

**Why have a code of conduct?**

Not because we feel like we're expected to have one, not because someone told us to, and not because we
heard somewhere that it was important for some reason -- but **as part
of an intentional effort to define the culture of Toronto JS**.

<br />

## THE SHORT VERSION

Toronto JS is dedicated to providing a harassment-free environment for everyone, regardless of their gender, sexual orientation,
disability, physical appearance, body size, race, or religion. This community welcomes participation from people of different skill levels, orientations and backgrounds.

All communication should be appropriate for a professional audience, including people of many different backgrounds. Discriminatory language and imagery is not appropriate for this community and its events, including Toronto JS Social, JS Tech Talk, JS Code Club and JS Workshop.

We do not tolerate bullying, name-calling, personal attacks, harassment, threats, or discriminatory behaviour that can create an unsafe environment for members from marginalized groups, including discriminatory jokes, suggestions of intellectual superiority or intolerance towards accessibility or diversity efforts.

Members violating these rules may be asked to leave an event or denied access to the Toronto JS online community
at the sole discretion of the organizers.

Thank you for helping us create a welcoming space for all.

<br />

## THE LONGER VERSION

### Welcoming behavior

Examples of behavior that contributes to creating a positive environment include:

- Using welcoming and inclusive language
- Calling people by preferred pronouns
- Being respectful of differing viewpoints and experiences
- Gracefully accepting constructive criticism
- [Disagreeing productively](https://medium.com/thinking-is-hard/guidelines-for-fruitful-dialogue-9d7d0b46da89)
- Focusing on what is best for the community
- Showing empathy towards other community members

### Unacceptable behavior

The following types of behavior are unacceptable at Toronto JS, both online and in-person, and constitute code of conduct violations.

### Abusive behavior

- Harassment: offensive verbal comments related to gender,
  sexual orientation, disability, physical appearance, body size, race,
  religion, sexual images in public spaces, deliberate intimidation,
  stalking, following, harassing photography or recording, sustained
  disruption of talks or other events, inappropriate physical contact or telecommunication,
  derisive comments regarding technical background, and unwelcome sexual or romantic attention.

- Threats: threatening someone physically or verbally. For example, threatening to publicize sensitive information about someone’s personal life.

### Unwelcoming behavior

- Blatant -isms –saying things that are explicitly racist, sexist, homophobic, etc. For example, arguing that some people are less intelligent because of their gender, race or religion. Subtle -isms and small mistakes made in conversation are not code of conduct violations. However, repeating something after it has been pointed out to you that you broke a social rule, or antagonizing or arguing with someone who has pointed out your subtle -ism is considered unwelcoming behavior, and is not allowed at Toronto JS.

- Maliciousness towards other community members—deliberately attempting to make others feel bad, name-calling, singling out others for derision or exclusion. For example, telling someone they’re not a real programmer or that they don’t belong at Toronto JS.

- Being especially unpleasant or elitist—for example, if we’ve received reports from multiple members or organizer of annoying, rude, or especially distracting behavior.

Participants asked to stop any harassing or malicious behavior are expected to comply immediately.

Be careful in the words that you choose. Excessive swearing, trolling or the incessant posting of inflammatory comments, sexist, homophobic, transphobic, racist, ableist or otherwise exclusionary jokes can be offensive and hostile towards those around you.

If a participant engages in behavior that violates our anti-harassment policy or code of conduct, Toronto JS organizers may take any action they deem appropriate, including warning the offender or expulsion from the
event, gathering or Toronto JS online community.

Moderators have the right and responsibility to remove comments or other contributions that are not aligned to this Code of Conduct, or to suspend temporarily or permanently any members for other behaviors that they deem inappropriate, threatening, offensive, or harmful.

<br />

## SOCIAL RULES

In addition to having a code of conduct as an anti-harassment policy,
we have a small set of [social rules](https://www.recurse.com/manual#sub-sec-social-rules) we
follow. We, the organizers, have adapted rules from
[the Recurse Center](https://www.recurse.com/) because we felt that
they contribute enormously to a supportive, productive, and fun
learning environment, and we'd like Toronto JS to share that environment.

These rules are intended to be lightweight, and to make more explicit certain social norms that are normally implicit. Most of our social rules really boil down to "don't be a jerk" or "don't be annoying." Of course, almost nobody sets out to be a jerk or annoying, so telling people not to be jerks isn't a very productive strategy. That's why our social rules are designed to curtail specific behavior we've found to be destructive to a supportive, productive, and fun learning environment.

### No feigning surprise

The first rule means you shouldn't act surprised when people say they don't know something. This applies to both technical things ("What?! I can't believe you don't know what the stack is!") and non-technical things ("You don't know who RMS is?!"). Feigning surprise has absolutely no social or educational benefit: When people feign surprise, it's usually to make them feel better about themselves and others feel worse. And even when that's not the intention, it's almost always the effect. As you've probably already guessed, this rule is tightly coupled to our belief in the importance of people feeling comfortable saying "I don't know" and "I don't understand."

### No well-actually's

A well-actually happens when someone says something that's almost - but not entirely - correct, and you say, "well, actually…" and then give a minor correction. This is especially annoying when the correction has no bearing on the actual conversation. This doesn't mean that Toronto JS isn't about truth-seeking or that we don't care about being precise. Almost all well-actually's in our experience are about grandstanding, not truth-seeking. (Source: [Miguel de Icaza](http://tirania.org/))

### No back-seat driving (in irl, face-to-face contexts)

If you overhear people working through a problem, please don't intermittently lob advice across the room. This can lead to a "too many cooks" problem, but more important, it can be rude and disruptive to half-participate in a conversation. This isn't to say you shouldn't help, offer advice, or join conversations. On the contrary, we encourage all those things. Rather, it just means that when you help or work with others, you should fully engage and not butt in sporadically.

### No subtle "-isms"

Our last social rule bans subtle racism, sexism, homophobia, transphobia, ableism and other kinds of bias. This one is different from the rest, because it covers a class of behaviors instead of one very specific pattern.

Subtle -isms are small things that make others feel unwelcome, things that we all sometimes do by mistake. For example, saying "It's so easy my grandmother could do it" is a subtle -ism. Like the other three social rules, this one is often accidentally broken. Like the other three, it's not a big deal to mess up – you just apologize and move on.

If you see a subtle -ism happen at Toronto JS, you can point it out to the relevant person, either publicly or privately, or you can ask one of organizers to say something. After this, we ask that all further discussion move off of public channels. If you are a third party, and you don't see what could be biased about the comment that was made, feel free to talk to the organizers. Please don't say, "Comment X wasn't homophobic!" Similarly, please don't pile on to someone who made a mistake. The "subtle" in "subtle -isms" means that it's probably not obvious to everyone right away what was wrong with the comment.

Moderators will not act on complaints regarding:

- ‘Reverse’ -isms, including ‘reverse racism,’ ‘reverse sexism,’ and ‘cisphobia’
- Reasonable communication of boundaries, such as 'leave me alone,' 'go away,' or 'I’m not discussing this with you.'
- Someone’s refusal to explain or debate social justice concepts
- Criticisms of racist, sexist, cissexist, or otherwise oppressive behavior or assumptions

We want Toronto JS to be a space with as little bigotry or oppression as possible. Therefore, if you see sexism, racism, or outrage at such things outside Toronto JS and also do not experience such issues directly yourself, please don't bring it in for discussion. For example, don't start a discussion of the latest offensive comment from Random Tech Person Y. For many people, especially those who may have spent time in unpleasant work environments, these conversations can turn into long threads that become distracting and disheartening. At Toronto JS, we want to remove as many distractions as possible for marginalized people so they can focus on enjoying programming. There are many places in the world to discuss and debate these issues, but there are few where they can avoid them. We want Toronto JS to be one of those places.

### Why have social rules?

The goal isn't to burden everyone with a bunch of annoying rules, or to give us a stick to bludgeon people with for "being bad." Rather, these rules are designed to help all of us build a pleasant, productive, and fearless community.

If someone says, "hey, you just feigned surprise," or "that's subtly sexist," don't worry. Just apologize, reflect for a second, and move on. It doesn't mean you're a "bad" person, or even a "bad" community member. As we said above, these rules are meant to be guidelines. We've all done these things before.

Sometimes pointing out that someone broke a social rule can be challenging or feel awkward. If you don't feel comfortable pointing out to someone that they are consistently breaking a social rule, ask an organizer or moderator for help.

If you have any questions about any part of the code of conduct or social rules, please feel free to reach out to any of
organizers or moderators.

<br />

## CONTACT INFORMATION

If you have any concerns around social conduct or notice that someone may be the target harassment or oppressive behaviour, please reach out to a Toronto JS organizer or volunteer. Please contact
[a member of Toronto JS organizers or volunteer staff](/p/volunteers). You
can use the below form to contact us, with or without your name.

Organizers, moderators and volunteers will be happy to do whatever we can to help those
experiencing harassment to feel safe during events and at all times in our online community. We value your attendance.

<br />

## REPORT A VIOLATION <br />(with or without your name)

Please provide the following information:

- the time and place the harassment took place
- the member(s) involved in the event
- any surrounding details you think are relevant to the report

<form
  action="https://formspree.io/f/mayvyvzk"
  method="POST"
  width="100%"
>
  <label htmlFor="_replyto">
    Email
    <input type="email" name="_replyto"/>
  </label>
  <br/>
  <label>
    Description
    <br/>
    <textarea name="message" rows="5"></textarea>
  </label>
  <br/>
  <button type="submit">Send</button>
</form>


## LICENSE

The Toronto JS Code of Conduct is under a
[Creative Commons Zero](http://creativecommons.org/about/cc0) license.
Our Code of Conduct was adapted from the [Dev.to Code Of Conduct](https://dev.to/code-of-conduct)
It was forked from the
[!!Con Code of Conduct](http://bangbangcon.com/conduct.html),
which is also licensed under a
[Creative Commons Zero](http://creativecommons.org/about/cc0) license,
and which itself was forked from
[PyCon 2013 Code of Conduct](https://us.pycon.org/2013/about/code-of-conduct/),
which is licensed under a
[Creative Commons Attribution 3.0 Unported License](http://creativecommons.org/licenses/by/3.0/),
and which itself was forked from
[an example policy from the Geek Feminism wiki, created by the Ada Initiative and other volunteers](http://geekfeminism.wikia.com/wiki/Conference_anti-harassment/Policy)
and available under a
[Creative Commons Zero](http://creativecommons.org/about/cc0) license.
